/**
 * @fileoverview Edit User form schema.
 * @description Client-side checks for the Edit User form, mirroring the backend's
 * UpdateUserRequestDto: first and last name required (100 characters each), an optional middle
 * name, and a 10-digit Indian mobile number. A number already on file can be changed but not
 * removed (the backend has no way to clear it), and is accepted unchanged even when it doesn't
 * match the Indian format. Role and reporting manager are required only when they're editable
 * (an ACTIVE user). The state is optional: blank means All India. Messages are translation keys.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { z } from 'zod'

import { MOBILE_NUMBER_PATTERN } from '@/shared/lib/validation/contact-fields.constants'

const NAME_MAX_LENGTH = 100

interface EditUserFormContext {
  /** The mobile number on file, or '' for none. */
  currentMobileNumber: string
  /** Whether the role and reporting manager can be changed. */
  canChangePlacement: boolean
}

/**
 * @description Builds the Edit User schema for the user being edited.
 * @param {EditUserFormContext} context - The number on file, and whether placement is editable.
 * @returns {z.ZodType} The form schema.
 */
export function buildEditUserFormSchema({
  currentMobileNumber,
  canChangePlacement,
}: EditUserFormContext) {
  const required = (message: string) =>
    canChangePlacement ? z.string().min(1, message) : z.string()

  return z.object({
    firstName: z
      .string()
      .trim()
      .min(1, 'sales.userManagement.editUserForm.errors.firstNameRequired')
      .max(NAME_MAX_LENGTH, 'sales.userManagement.editUserForm.errors.nameTooLong'),
    middleName: z
      .string()
      .trim()
      .max(NAME_MAX_LENGTH, 'sales.userManagement.editUserForm.errors.nameTooLong'),
    lastName: z
      .string()
      .trim()
      .min(1, 'sales.userManagement.editUserForm.errors.lastNameRequired')
      .max(NAME_MAX_LENGTH, 'sales.userManagement.editUserForm.errors.nameTooLong'),
    mobileNumber: z
      .string()
      .trim()
      .refine((value) => value !== '' || currentMobileNumber === '', {
        message: 'sales.userManagement.editUserForm.errors.mobileRequired',
      })
      .refine(
        (value) =>
          value === '' || value === currentMobileNumber || MOBILE_NUMBER_PATTERN.test(value),
        { message: 'validation.mobileNumberInvalid' },
      ),
    // A state id, or '' for All India.
    stateId: z.string(),
    primaryRoleId: required('sales.userManagement.editUserForm.errors.roleRequired'),
    reportingManagerUserId: required('sales.userManagement.editUserForm.errors.reportsToRequired'),
  })
}

export type EditUserFormValues = z.infer<ReturnType<typeof buildEditUserFormSchema>>
